import type { TodayCalendar, TodayScope, TodaySummary as TodaySummaryDto } from '@fos/shared/contracts/today';
import { useQuery } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { trpc } from '../../lib/trpc';

/**
 * Dane ekranu Dziś: `today.summary({ scope })` (Dzień: kafelki, „Wymaga Ciebie”, zadania „Plan na dziś”) i
 * `today.calendar({ scope, from, to })` (Tydzień, Miesiąc i spotkania dnia w „Plan na dziś”) z tRPC
 * (packages/core/src/modules/today), przełącznik „Moje / Zespół” z menu bocznego (`useScope`). Kształty:
 * `@fos/shared/contracts/today`. Testy i tryb podglądu (`?mock=demo`) podmieniają źródła przez `setTodaySource`
 * i `setCalendarSource`.
 */
export type TodaySummary = TodaySummaryDto;

export const EMPTY_TODAY: TodaySummary = {
  scope: 'mine',
  approvals: { pending: 0, oldestWaitingSec: null },
  tasks: { today: 0, overdue: 0 },
  alarms: { open: 0, firstTitle: null, incidents: 0, firstIncidentId: null, firstSimulated: false },
  unbilled: null,
  nextMeeting: null,
  needsYou: [],
  plan: [],
};

export type TodaySource = (input: { scope: TodayScope }) => Promise<TodaySummary>;

let override: TodaySource | null = null;

/** Podmienia źródło danych (testy, tryb podglądu). `null` = tRPC. */
export function setTodaySource(next: TodaySource | null): void {
  override = next;
}

export const TODAY_QUERY_KEY = ['today', 'summary'] as const;

export function useTodaySummary() {
  const { scope } = useScope();
  const utils = trpc.useUtils();
  return useQuery({
    queryKey: [...TODAY_QUERY_KEY, scope],
    queryFn: () => (override ? override({ scope }) : utils.client.today.summary.query({ scope })),
    staleTime: 30_000,
  });
}

// ── Tydzień / Miesiąc ───────────────────────────────────────────────────────

export type CalendarSource = (input: { scope: TodayScope; from: string; to: string }) => Promise<TodayCalendar>;

let calendarOverride: CalendarSource | null = null;

/** Podmienia źródło `today.calendar` (testy, tryb podglądu). `null` = tRPC. */
export function setCalendarSource(next: CalendarSource | null): void {
  calendarOverride = next;
}

export const TODAY_CALENDAR_QUERY_KEY = ['today', 'calendar'] as const;

/** Pusty kalendarz zakresu (tryb podglądu bez danych, stan przed wczytaniem). */
export function emptyCalendar(input: { scope: TodayScope; from: string; to: string }, today: string): TodayCalendar {
  return { ...input, today, items: [], days: null, unbilled: null };
}

export function useTodayCalendar(range: { from: string; to: string }, opts: { enabled?: boolean } = {}) {
  const { scope } = useScope();
  const utils = trpc.useUtils();
  const input = { scope, from: range.from, to: range.to };
  return useQuery({
    queryKey: [...TODAY_CALENDAR_QUERY_KEY, scope, range.from, range.to],
    queryFn: () => (calendarOverride ? calendarOverride(input) : utils.client.today.calendar.query(input)),
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  });
}
